export const APPLICATION_TIME_ZONE =
  "Europe/Samara";

export const APPLICATION_TIME_ZONE_LABEL =
  "Самара · UTC+4";

const APPLICATION_LOCALE = "ru-RU";

interface ZonedClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function getPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): number {
  const value = parts.find(
    (part) => part.type === type,
  )?.value;

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    throw new Error(
      `Не удалось получить часть даты: ${type}`,
    );
  }

  return parsedValue;
}

export function getSamaraClockParts(
  value: Date = new Date(),
): ZonedClockParts {
  const parts = new Intl.DateTimeFormat(
    "en-CA",
    {
      timeZone: APPLICATION_TIME_ZONE,

      year: "numeric",
      month: "2-digit",
      day: "2-digit",

      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",

      hourCycle: "h23",
    },
  ).formatToParts(value);

  return {
    year: getPart(parts, "year"),
    month: getPart(parts, "month"),
    day: getPart(parts, "day"),
    hour: getPart(parts, "hour"),
    minute: getPart(parts, "minute"),
    second: getPart(parts, "second"),
  };
}

export function formatTime(
  value: string | null,
): string {
  if (!value) {
    return "Без времени";
  }

  return new Intl.DateTimeFormat(
    APPLICATION_LOCALE,
    {
      timeZone: APPLICATION_TIME_ZONE,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    },
  ).format(new Date(value));
}

export function formatCurrentDate(
  value: Date = new Date(),
): string {
  const formattedValue =
    new Intl.DateTimeFormat(
      APPLICATION_LOCALE,
      {
        timeZone: APPLICATION_TIME_ZONE,
        weekday: "long",
        day: "numeric",
        month: "long",
      },
    ).format(value);

  return (
    formattedValue.charAt(0).toUpperCase()
    + formattedValue.slice(1)
  );
}

export function formatSamaraDateTime(
  value: string | Date,
): string {
  const date = (
    value instanceof Date
      ? value
      : new Date(value)
  );

  return new Intl.DateTimeFormat(
    APPLICATION_LOCALE,
    {
      timeZone: APPLICATION_TIME_ZONE,

      year: "numeric",
      month: "2-digit",
      day: "2-digit",

      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",

      hourCycle: "h23",
    },
  ).format(date);
}

export function getWorkdayProgress(
  value: Date = new Date(),
): number {
  const samaraTime =
    getSamaraClockParts(value);

  const currentSeconds = (
    samaraTime.hour * 60 * 60
    + samaraTime.minute * 60
    + samaraTime.second
  );

  const workdayStartSeconds =
    9 * 60 * 60;

  const workdayEndSeconds =
    20 * 60 * 60;

  if (currentSeconds <= workdayStartSeconds) {
    return 0;
  }

  if (currentSeconds >= workdayEndSeconds) {
    return 100;
  }

  return Math.round(
    (
      (
        currentSeconds
        - workdayStartSeconds
      )
      / (
        workdayEndSeconds
        - workdayStartSeconds
      )
    ) * 100,
  );
}

export function isoToSamaraDateTimeInput(
  value: string | null,
): string {
  if (!value) {
    return "";
  }

  const parts = getSamaraClockParts(
    new Date(value),
  );

  return [
    String(parts.year).padStart(4, "0"),
    "-",
    String(parts.month).padStart(2, "0"),
    "-",
    String(parts.day).padStart(2, "0"),
    "T",
    String(parts.hour).padStart(2, "0"),
    ":",
    String(parts.minute).padStart(2, "0"),
  ].join("");
}

export function formatSamaraDateTimeShort(
  value: string | null,
): string {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ru-RU",
    {
      timeZone: APPLICATION_TIME_ZONE,

      day: "2-digit",
      month: "2-digit",
      year: "numeric",

      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",

      hourCycle: "h23",
    },
  ).format(new Date(value));
}

export function getDefaultSamaraDateTimeInput(
  value: Date = new Date(
    Date.now() + 60 * 60 * 1000,
  ),
): string {
  const roundedDate = new Date(value);

  roundedDate.setUTCMinutes(
    Math.ceil(
      roundedDate.getUTCMinutes() / 15,
    ) * 15,
    0,
    0,
  );

  const parts =
    getSamaraClockParts(roundedDate);

  return [
    String(parts.year).padStart(4, "0"),
    "-",
    String(parts.month).padStart(2, "0"),
    "-",
    String(parts.day).padStart(2, "0"),
    "T",
    String(parts.hour).padStart(2, "0"),
    ":",
    String(parts.minute).padStart(2, "0"),
  ].join("");
}

export function samaraLocalDateTimeToIso(
  value: string,
): string | null {
  const normalizedValue = value.trim();

  if (normalizedValue.length === 0) {
    return null;
  }

  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(
      normalizedValue,
    )
  ) {
    throw new Error(
      "Некорректная дата или время.",
    );
  }

  const date = new Date(
    `${normalizedValue}:00+04:00`,
  );

  if (Number.isNaN(date.getTime())) {
    throw new Error(
      "Некорректная дата или время.",
    );
  }

  return date.toISOString();
}
